import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { usePedir } from '@xiltrion/ui/bloques/pedir';
import { EstadoCargando, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { Cronograma } from '@xiltrion/ui/bloques/cronograma';
import { ProgresoCircular, Semaforo } from '@xiltrion/ui/bloques/indicadores';
import { SelectorMultiple } from '@xiltrion/ui/bloques/selectores';
import { Tabla } from '@xiltrion/ui/bloques/tabla';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent } from '@xiltrion/ui/card';
import { Icono } from '@xiltrion/ui/icono';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { apiGet, apiPatch, apiPost } from '@/api/client';
import { avisarError, useProjects } from '@/api/hooks';
import type { ProjectRow } from '@/api/types';
import { useTerms } from '@/app/session';
import { formatDate } from '../tasks/view-logic';

interface Portfolio { id: string; name: string; description: string | null; projectIds: string[] }

export function Portfolios() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t, nuevo } = useTerms();
  const { data = [], isLoading } = useQuery({ queryKey: ['portfolios'], queryFn: () => apiGet<Portfolio[]>('/portfolios') });
  const { pedir, dialogo } = usePedir();
  return (
    <div className="space-y-6 p-6">
      <EncabezadoPagina titulo={t('portfolio', true)} descripcion="Grupos de proyectos con su avance, semáforo y cronograma." acciones={<Button onClick={async () => { const name = await pedir({ titulo: nuevo('portfolio'), etiqueta: 'Nombre', textoConfirmar: 'Crear' }); if (name) void apiPost<Portfolio>('/portfolios', { name }).then((p) => { void qc.invalidateQueries({ queryKey: ['portfolios'] }); navigate(`/portafolios/${p.id}`); }, avisarError); }}><Icono nombre="mdi:plus" />{nuevo('portfolio')}</Button>} />
      {isLoading ? <EstadoCargando /> : data.length === 0 ? <EstadoVacio titulo={`Aún no hay ${t('portfolio', true).toLowerCase()}`} borde /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((p) => <Card key={p.id} className="cursor-pointer hover:border-ring/50" onClick={() => navigate(`/portafolios/${p.id}`)}><CardContent className="space-y-1 pt-6"><div className="flex items-center gap-2 font-medium"><Icono nombre="mdi:briefcase-outline" />{p.name}</div><div className="text-sm text-muted-foreground">{p.projectIds.length} proyectos</div></CardContent></Card>)}
        </div>
      )}
      {dialogo}
    </div>
  );
}

export function PortfolioPage() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: all = [] } = useProjects();
  const { data, isLoading } = useQuery({ queryKey: ['portfolio', portfolioId], queryFn: () => apiGet<Portfolio & { projects: ProjectRow[] }>(`/portfolios/${portfolioId}`) });
  if (isLoading || !data) return <div className="p-6"><EstadoCargando /></div>;
  const avg = data.projects.length ? data.projects.reduce((a, p) => a + p.progress, 0) / data.projects.length : 0;
  return (
    <div className="space-y-6 p-6">
      <EncabezadoPagina titulo={data.name} descripcion={`${data.projects.length} proyectos · avance promedio ${Math.round(avg * 100)}%`} acciones={<div className="w-72"><SelectorMultiple value={data.projectIds} onChange={(ids) => void apiPatch(`/portfolios/${data.id}`, { projectIds: ids }).then(() => qc.invalidateQueries({ queryKey: ['portfolio', portfolioId] }), avisarError)} options={all.map((p) => ({ value: p.id, label: p.name }))} placeholder="Proyectos del portafolio" /></div>} />
      <Tabs defaultValue="list">
        <TabsList variant="line"><TabsTrigger value="list"><Icono nombre="mdi:table" />Lista</TabsTrigger><TabsTrigger value="timeline"><Icono nombre="mdi:chart-gantt" />Cronograma</TabsTrigger></TabsList>
        <TabsContent value="list" className="pt-4">
          <Tabla filas={data.projects} claveFila={(p) => p.id} onClicFila={(p) => navigate(`/proyectos/${p.id}`)} columnas={[
            { key: 'name', titulo: 'Proyecto', valor: (p) => p.name },
            { key: 'progress', titulo: 'Avance', valor: (p) => p.progress, render: (p) => <span className="inline-flex items-center gap-2"><ProgresoCircular valor={p.progress} tamano={30} grosor={3} color={p.trafficLight} /><Semaforo color={p.trafficLight} conTexto /></span> },
            { key: 'tasks', titulo: 'Tareas', valor: (p) => p.stats.total, numerico: true, render: (p) => `${p.stats.done}/${p.stats.total}` },
            { key: 'overdue', titulo: 'Vencidas', valor: (p) => p.stats.overdue, numerico: true },
            { key: 'end', titulo: 'Fin', valor: (p) => p.dueDate ?? '', render: (p) => formatDate(p.dueDate, true) || '—' },
          ]} />
        </TabsContent>
        <TabsContent value="timeline" className="pt-4">
          <Cronograma escala="month" barras={data.projects.filter((p) => p.startDate || p.dueDate).map((p) => ({ id: p.id, titulo: p.name, inicio: p.startDate, fin: p.dueDate, avance: p.progress, color: p.color }))} onAbrir={(id) => navigate(`/proyectos/${id}`)} alto={Math.max(240, 60 + data.projects.length * 34)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
